import {
  sendDonationToTVault,
  getTVaultDonationStatus,
} from '../actions/tvaultActions'
import { getDonationPayload } from './helpers'
import { RootObject } from '../types/InAppDonationTypes/comcastDonationTypes'
import { SubscriptionDataPayload } from '../types/InAppDonationTypes/subscriptionDonationTypes'
import { TransactionDataPayload } from '../types/InAppDonationTypes/transactionDonationTypes'
import { ConsentParams } from '../types/InAppDonationTypes/consentParamsTypes'
import {
  SubscriptionData,
  SubscriptionResponse,
} from '../types/InAppDonationTypes/tvaultSubscriptionReponseTypes'
import {
  TransactionData,
  TransactionResponse,
} from '../types/InAppDonationTypes/tvaultTransactionResponseTypes'
import {
  AckGatewayDonationResponse,
  PollGatewayDonationResponse,
} from '../types/InAppDonationTypes/tvaultBaseResponseTypes'
import PBSError, { IError } from '../../../shared/PBSError'
import { store } from '../store.js'
import { platformStoreSet } from '../../../shared/helpers.js'
import { MONTHLY } from './ComcastBilling.js'

type DonationResponse = TransactionResponse | SubscriptionResponse
export const APPROVED = 'approved'
export const DECLINED = 'declined'

type TDonationInformation = {
  donationCampaignId: string
  totalChargeAmount: number
  consentToken: string
  oat: string
}

class TVaultDonationService {
  private donationFrequency: string
  private donationPayload:
    | RootObject<SubscriptionDataPayload>
    | RootObject<TransactionDataPayload>
  private campaignStation: string
  private maxDonationRetryCall = 12
  private retryTimeoutId: NodeJS.Timeout | null = null
  private retryCount = 0
  private retryMs = 5000

  constructor(campaignStation: string) {
    this.campaignStation = campaignStation
  }

  public triggerProcess = async () => {
    try {
      const response: DonationResponse = await sendDonationToTVault(
        this.donationFrequency,
        this.campaignStation,
        this.donationPayload
      )

      if (this.isDesiredStatus(response as DonationResponse)) {
        return { donationResponse: response }
      }

      const donationId = this.getDonationId(response)

      return await this.setPollingProcess(donationId)
    } catch (e) {
      const pbsError = new PBSError(e, 'TVAULT_ERROR')

      const state = store.getState()
      PBSError.sendSentryError({
        errorString: 'TVault service error',
        errorTitle: 'TVaultDonationService.ts',
        extraParams: {
          errorCodeNumber: pbsError.errorStatus,
          errorCodeText: e.name,
          errorMessage: e.message,
          userPid: state.user.pid,
          partnerID: state.device.distributor,
        },
      })
      throw pbsError
    }
  }

  public setDonationFrequency(donationFrequency: string) {
    this.donationFrequency = donationFrequency
  }

  public setDonationPayload(
    consentParams: ConsentParams,
    donationInformation: TDonationInformation
  ) {
    this.donationPayload = getDonationPayload(consentParams, {
      ...donationInformation,
      donationFrequency: this.donationFrequency,
    })
  }

  private getBaseGatewayResponse = (donationResponse: DonationResponse) => {
    const donationResponseData = this.getFirstDonationData(donationResponse)

    const gatewayType =
      this.donationFrequency === MONTHLY ? 'subscription' : 'transaction'
    return donationResponseData.attributes[
      `gateway_${gatewayType}_response`
    ] as AckGatewayDonationResponse | PollGatewayDonationResponse
  }

  private getDonationPollGatewayResponse = (
    donationResponse: DonationResponse
  ) => {
    const response = this.getBaseGatewayResponse(donationResponse)

    if (!Array.isArray(response)) {
      return response as PollGatewayDonationResponse
    }

    return ''
  }

  public getDonationResponseStatus = (donationResponse: DonationResponse) => {
    const response = this.getDonationPollGatewayResponse(
      donationResponse
    ) as PollGatewayDonationResponse

    return response.status
  }

  public getDonationDeclinedReason = (donationResponse: DonationResponse) => {
    const response = this.getDonationPollGatewayResponse(
      donationResponse
    ) as PollGatewayDonationResponse

    return response.declineReason?.replace(/[']/g, '')
  }

  public getDonationId = (donationResponse: DonationResponse) => {
    const donationResponseData = this.getFirstDonationData(donationResponse) as
      | TransactionData
      | SubscriptionData

    return donationResponseData.id
  }

  public clearTimeout = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
      this.retryTimeoutId = null
      this.retryCount = 0
    }
  }

  private getFirstDonationData = (
    donationResponse: DonationResponse
  ): TransactionData | SubscriptionData => {
    if (Array.isArray(donationResponse.data)) {
      return donationResponse.data[0]
    }
    return donationResponse.data
  }

  public configureMvaultId = (donationResponse: DonationResponse) => {
    const mvaultId = this.getMvaultId(donationResponse)
    this.setMvaultId(mvaultId)
  }

  private getMvaultId = (donationResponse: DonationResponse) => {
    const donationResponseData = this.getFirstDonationData(donationResponse) as
      | TransactionData
      | SubscriptionData
    return donationResponseData.attributes.mvault_id
  }

  private setMvaultId = (mvaultId: string) => {
    platformStoreSet('mvault_id', mvaultId)
  }

  private isDesiredStatus = (donationResponse: DonationResponse) => {
    const status = this.getDonationResponseStatus(donationResponse)
    if ([APPROVED, DECLINED].includes(status)) return true
    return false
  }

  private setPollingProcess = async (donationId: string) => {
    return new Promise((resolve, reject) => {
      const pollTransactionStatus = async () => {
        this.retryCount++

        try {
          const donationResponse = await getTVaultDonationStatus(
            this.donationFrequency,
            this.campaignStation,
            donationId
          )
          if (this.isDesiredStatus(donationResponse)) {
            this.clearTimeout()
            resolve({ donationResponse })
          } else if (this.retryCount === this.maxDonationRetryCall) {
            this.clearTimeout()
            const error: IError = new Error(
              'Maximum attempts reached without desired status.'
            )
            error.status = 408 // TODO: polling error has no code, then a timed out is assumed, needs to be defined
            reject(error)
          } else {
            // Schedule the next poll
            this.retryTimeoutId = setTimeout(
              pollTransactionStatus,
              this.retryMs
            )
          }
        } catch (err) {
          reject(err)
        }
      }

      pollTransactionStatus()
    })
  }
}

export default TVaultDonationService
